import axios, { AxiosInstance, InternalAxiosRequestConfig, AxiosResponse } from 'axios';
import { API_URL, DEBUG, isDevelopment } from '../config/env';
import { getAuthToken, removeAuthToken } from '../utils/authToken';

// In development, use empty baseURL to let Vite proxy handle requests
// In production, use the full API URL
const baseURL = isDevelopment ? '' : API_URL;

// Create axios instance with base configuration
const apiClient: AxiosInstance = axios.create({
  baseURL: baseURL,
  timeout: 10000, // 10 seconds
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - add auth token (one token for both user and guest), logging, etc.
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Always send token if present (user or guest JWT from authToken)
    const token = getAuthToken();
    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Debug logging
    if (DEBUG) {
      console.log(`[API Request] ${config.method?.toUpperCase()} ${config.url}`, config.data);
    }

    return config;
  },
  (error) => {
    if (DEBUG) {
      console.error('[API Request Error]', error);
    }
    return Promise.reject(error);
  }
);

// Response interceptor - handle errors, logging, etc.
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    // Debug logging
    if (DEBUG) {
      console.log(`[API Response] ${response.config.method?.toUpperCase()} ${response.config.url}`, response.data);
    }
    return response;
  },
  (error) => {
    // Handle common errors
    if (error.response) {
      // Server responded with error status
      if (DEBUG) {
        console.error('[API Response Error]', error.response.status, error.response.data);
      }
      
      // Handle specific status codes
      if (error.response.status === 401) {
        const url = String(error.config?.url ?? '');
        // Guest refresh failure: caller (e.g. AuthProvider) may fall back to new guest — do not
        // strip the token or force login here.
        if (url.includes('/auth/guest-session/refresh')) {
          return Promise.reject(error);
        }
        // Unauthorized - clear token and redirect to login
        removeAuthToken();
        // Only redirect if not already on login page
        if (window.location.pathname !== '/login' && window.location.pathname !== '/createaccount') {
          window.location.href = '/login';
        }
      }
    } else if (error.request) {
      // Request made but no response received
      if (DEBUG) {
        console.error('[API Network Error]', error.request);
      }
    } else {
      // Something else happened
      if (DEBUG) {
        console.error('[API Error]', error.message);
      }
    }

    return Promise.reject(error);
  }
);

export default apiClient;

